// Zenith Orb look (owner 2026-11-02: 'change colors closer to images'; 'explosion impact should send rocks flying outward
// as it creates the crater'; 'make the zenith orb explosion a dome shape that expands a bit upon impact, and start as a
// smaller size and the dome gets larger each rank'). Render-only: reads Sim projectiles / SimEvents, never feeds back.
//  - orb palette (charging + thrown + split orbs): blown-out white core → aqua → cyan body → crisp light-cyan rim, then a
//    deeper sky-blue / azure glow ring outside it, with thin white-cyan filaments crawling over the surface. Hard bands.
//  - impact: a white flash, then a cyan ENERGY DOME (hemisphere on the ground, radius = the sim blast radius, so it grows
//    every rank) that pops up, swells a little, holds translucent and thins out by ~1.3 s; a shock ring with a blue-violet
//    edge, a dust ring at its base, and chunky low-poly rocks thrown out of the crater (BlastFx `Rocks` pool with a hot
//    cyan rim that cools off), tinted from the local ground. The crater itself is carved by the Sim terrain crater.
import * as THREE from 'three/webgpu';
import { abs, attribute, color, cos, dot, float, mix, mx_noise_float, normalView, positionGeometry, positionLocal, positionViewDirection, sin, smoothstep, time, uniform, vec3 } from 'three/tsl';
import { CROWD, type Fx } from './fx';
import { Rocks } from './blastFx';
import { GROUND_HALF, GROUND_RES, type GroundDeform } from './ground';
import type { GroundGlow } from './groundGlow';
import type { RenderStyle } from './toon';
import { DEBRIS, DEFAULT_DEBRIS } from './debrisLook';

const mkGain = () => uniform(1.35);
type Gain = ReturnType<typeof mkGain>;

/** Zenith palette (colours only, taken from the owner's reference images) */
export const ZEN = {
  white: 0xffffff, aqua: 0x7ff4ff, cyan: 0x2fd8ff, rim: 0xc8fdff, sky: 0x1e8fff, azure: 0x0a4fd0, violet: 0x6a5cff, fil: 0xeafeff,
  /** ground pool: a little deeper than the orb so it doesn't haze the grass */ pool: 0x1890ff,
} as const;
/** Sim explode colour of the main Zenith blast (main.ts keys its sound off the same value) */
export const ZENITH_BLAST_COL = 0xbfeaff;
/** Sim explode colour of a non-comet cluster shell: only Worldheart's split orbs launch those (sim.ts launchShell(.., false, true)) */
export const ZENITH_SPLIT_COL = 0xff7a40;
/** Worldheart split orb in flight (kind 3 shell, not a Rift Mortar comet bomblet) */
export const isZenithSplit = (pr: { kind: number; hostile: boolean; comet: boolean; pierce: number }): boolean => pr.kind === 3 && !pr.hostile && !pr.comet && pr.pierce === 1;

/** Orb surface: bands by screen-space radius (rho = 0 centre → 1 silhouette) so the white core stays a core at every size. */
function orbMaterial(gain: Gain): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, fog: false });
  const d = abs(dot(normalView, positionViewDirection)), rho = d.mul(d).oneMinus().max(0).sqrt();
  const churn = mx_noise_float(positionLocal.mul(2.5).add(vec3(time.mul(1.5), time.mul(-2), 0)));
  const e = rho.add(churn.mul(0.035));
  // emissive (> 1) only in the blown-out core (bloom when Glow is on); the cyan bands stay saturated, never washed to white
  let c = mix(color(ZEN.white).mul(gain), color(ZEN.aqua), smoothstep(0.37, 0.4, e)); // blown-out white core
  c = mix(c, color(ZEN.cyan), smoothstep(0.53, 0.56, e));                         // aqua → bright cyan body
  c = mix(c, color(0x12b6ff), smoothstep(0.75, 0.78, e));                         // deeper cyan just inside the rim
  c = mix(c, color(ZEN.rim), smoothstep(0.86, 0.88, e));                          // crisp light-cyan rim band
  c = mix(c, color(ZEN.sky), smoothstep(0.95, 0.965, e));                         // sky-blue edge line
  // thin white-cyan electric filaments: zero-crossings of two crawling noise fields, kept off the core and the rim
  const n1 = mx_noise_float(positionLocal.mul(2.3).add(vec3(0, time.mul(1.6), time.mul(0.9))));
  const n2 = mx_noise_float(positionLocal.mul(3.9).add(vec3(time.mul(-1.2), 3.3, time.mul(1.4))));
  const fil = smoothstep(0.06, 0.015, abs(n1)).max(smoothstep(0.045, 0.01, abs(n2)).mul(0.85));
  c = mix(c, color(ZEN.fil).mul(gain), fil.mul(smoothstep(0.36, 0.46, rho)).mul(smoothstep(0.9, 0.84, rho)));
  m.colorNode = c;
  m.opacityNode = float(1);
  return m;
}

/** Halo sprite (sprite width = 3.4 × orb radius, so the orb edge sits at 0.588 of the half-width): nothing over the orb,
 *  a crisp light-cyan ring just outside it, then a cyan → sky blue → azure glow falling off outward. Normal-blended so
 *  the deep blues stay blue over bright grass. */
function haloTexture(): THREE.Texture {
  const S = 256, h = S / 2, c = document.createElement('canvas'); c.width = c.height = S; const g = c.getContext('2d')!;
  const grd = g.createRadialGradient(h, h, 0, h, h, h);
  grd.addColorStop(0, 'rgba(47,216,255,0)'); grd.addColorStop(0.57, 'rgba(47,216,255,0)');
  grd.addColorStop(0.6, 'rgba(47,216,255,0.8)'); grd.addColorStop(0.69, 'rgba(30,143,255,0.6)');
  grd.addColorStop(0.83, 'rgba(10,79,208,0.3)'); grd.addColorStop(1, 'rgba(10,79,208,0)');
  g.fillStyle = grd; g.fillRect(0, 0, S, S);
  g.strokeStyle = 'rgba(200,253,255,0.95)'; g.lineWidth = S * 0.016; g.beginPath(); g.arc(h, h, h * 0.64, 0, Math.PI * 2); g.stroke();
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
}

/**
 * Energy dome (owner 2026-11-02): a blinding hemisphere on the ground at the impact, radius = the sim blast radius.
 * Timeline: pops to ~75 % in 0.1 s, eases to 100 % by 0.35 s, creeps to ~110 % while it holds, gone by 1.3 s.
 * Body: a big blown-out white-hot core (~60 % of the dome toward the top/centre) → saturated cyan toward the rim → a
 * bright light-cyan fresnel edge and ground rim, thin white streaks swirling over it. Emissive well over the bloom
 * threshold with Glow on (core ×2.6, cyan body ×1.1 so it stays saturated under bloom); with Glow off the core stays white (×1.5, no bloom to spread it) and the cyan vivid. Near-solid until
 * ~0.35 s; from ~0.5 s the core shrinks and the dome fades centre-first (rim last), so foes inside show from ~0.6 s; at
 * the end an even fade with a fine noise edge erosion. An additive glow shell (1.15×) and a flat ring of light at the
 * ground contact bloom around it. Per-instance 'dm' = (heat 0..1, seed, centre alpha, rim alpha).
 */
class EnergyDome {
  readonly body: THREE.InstancedMesh; readonly shell: THREE.InstancedMesh; readonly ring: THREE.InstancedMesh;
  private n = 0; private d: Float32Array; private dm: THREE.InstancedBufferAttribute;
  private static S = 6; // x y z rFinal age seed
  static readonly LIFE = 1.3;
  /** emission gains (follow the Glow setting) */
  readonly gCore = uniform(2.6); readonly gBody = uniform(1.1);
  constructor(private cap: number) {
    this.d = new Float32Array(cap * EnergyDome.S);
    const geo = new THREE.SphereGeometry(1, 48, 18, 0, Math.PI * 2, 0, Math.PI / 2); // y 0 (ground) → 1 (top)
    this.dm = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4); this.dm.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('dm', this.dm);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const D = attribute('dm', 'vec4') as any;
    const heat = D.x, seed = D.y, aC = D.z, aR = D.w;
    // (positionGeometry: the unit hemisphere before the instance transform; positionLocal already includes it)
    const facing = abs(dot(normalView, positionViewDirection)), f = facing.oneMinus(), y = positionGeometry.y;
    // thin swirling streaks: noise in a frame that twists with height and turns over time (no angle seam)
    const tw = y.mul(2.2).add(time.mul(0.8)).add(seed.mul(6)), ct = cos(tw), st = sin(tw);
    const sx = positionGeometry.x.mul(ct).sub(positionGeometry.z.mul(st)), sz = positionGeometry.x.mul(st).add(positionGeometry.z.mul(ct));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sw = mx_noise_float(vec3(sx.mul(3.4), y.mul(4.2).sub(time.mul(0.9)), sz.mul(3.4).add(seed.mul(9)))) as any;
    const streak = smoothstep(0.045, 0.01, sw.abs());
    // hard bands; the core threshold follows the heat: ~60 % of the dome white-hot at first, shrinking from ~0.5 s
    const k = facing.mul(0.6).add(y.mul(0.4));
    const coreT = heat.mul(-0.33).add(0.95);
    const cyanB = color(0x0a8cff).mul(this.gBody), aquaB = color(0x14c8ff).mul(this.gBody), whiteB = color(ZEN.white).mul(this.gCore);
    let c = mix(cyanB, aquaB, smoothstep(coreT.sub(0.2), coreT.sub(0.17), k));
    c = mix(c, whiteB, smoothstep(coreT, coreT.add(0.03), k));
    c = mix(c, color(ZEN.fil).mul(this.gCore), streak.mul(0.7).mul(smoothstep(coreT.add(0.02), coreT.sub(0.06), k)));
    const edge = smoothstep(0.84, 0.87, f), base = smoothstep(0.07, 0.045, y);
    c = mix(c, color(ZEN.rim).mul(this.gBody.mul(1.3)), edge.max(base));                          // bright fresnel edge + ground rim
    const bm = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, fog: false });
    bm.colorNode = c;
    // centre-first fade (rim last): the view-facing middle uses aC, the silhouette / ground rim aR
    const rimW = smoothstep(0.35, 0.8, f).max(base);
    // fine noise edge erosion as the rim alpha runs out (no broad bands)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fine = (mx_noise_float(positionGeometry.mul(14).add(vec3(seed.mul(5), time.mul(0.6), 0))) as any).mul(0.5).add(0.5);
    const erode = smoothstep(aR.oneMinus().sub(0.04), aR.oneMinus().add(0.04), fine.mul(0.9).add(0.1));
    bm.opacityNode = mix(aC, aR, rimW).mul(erode);
    this.body = new THREE.InstancedMesh(geo, bm, cap);
    this.body.frustumCulled = false; this.body.count = 0; this.body.renderOrder = 3;
    // strong additive outer glow shell (~1.15×), brightest toward its silhouette, blooms around the dome
    const gm = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, fog: false });
    gm.colorNode = mix(color(ZEN.sky), color(ZEN.cyan), heat.mul(0.6).add(0.4)).mul(smoothstep(0.42, 0.9, f).mul(1.9)).mul(aR).mul(this.gBody); // ~0 over the body's face (keeps the cyan saturated), bright just outside its silhouette
    this.shell = new THREE.InstancedMesh(geo, gm, cap);
    this.shell.frustumCulled = false; this.shell.count = 0; this.shell.renderOrder = 4;
    // flat ring of light where the dome meets the ground (additive; radial falloff around the dome radius)
    const rg = new THREE.RingGeometry(0.7, 1.35, 72, 1).rotateX(-Math.PI / 2);
    rg.setAttribute('dm', this.dm);
    const rl = positionGeometry.xz.length(), rk = abs(rl.sub(1.02)).div(0.33).oneMinus().max(0);
    const rm = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, fog: false, side: THREE.DoubleSide });
    rm.colorNode = mix(color(ZEN.cyan), color(ZEN.rim), rk.pow(3)).mul(rk.pow(1.5)).mul(aR).mul(this.gCore);
    this.ring = new THREE.InstancedMesh(rg, rm, cap);
    this.ring.frustumCulled = false; this.ring.count = 0; this.ring.renderOrder = 4;
  }
  add(x: number, y: number, z: number, r: number): void {
    if (this.n >= this.cap) { this.n--; } // pool full: replace the newest-but-one (older ones are nearly gone anyway)
    const i = this.n++ * EnergyDome.S, d = this.d;
    d[i] = x; d[i + 1] = y; d[i + 2] = z; d[i + 3] = r; d[i + 4] = 0; d[i + 5] = Math.random();
  }
  clear(): void { this.n = 0; this.body.count = 0; this.shell.count = 0; this.ring.count = 0; }
  update(dt: number): void {
    const d = this.d, S = EnergyDome.S, F = this.dm.array as Float32Array;
    for (let k = 0; k < this.n; k++) {
      const i = k * S; d[i + 4] += dt;
      if (d[i + 4] >= EnergyDome.LIFE) { this.n--; if (k !== this.n) d.copyWithin(i, this.n * S, this.n * S + S); k--; }
    }
    for (let k = 0; k < this.n; k++) {
      const i = k * S, t = d[i + 4];
      // radius: pop (ease-out) → swell → creep → slight drift while fading
      const sc = t < 0.1 ? 0.75 * (1 - (1 - t / 0.1) ** 3)
        : t < 0.35 ? 0.75 + 0.25 * smooth((t - 0.1) / 0.25)
        : t < 0.85 ? 1 + 0.1 * (t - 0.35) / 0.5 : 1.1 + 0.04 * (t - 0.85) / 0.45;
      const R = Math.max(0.05, d[i + 3] * sc);
      _p.set(d[i], d[i + 1] - 0.15, d[i + 2]); _q.identity(); _s.set(R, R * 0.92, R);
      this.body.setMatrixAt(k, _m.compose(_p, _q, _s));
      _s.set(R * 1.15, R * 1.08, R * 1.15);
      this.shell.setMatrixAt(k, _m.compose(_p, _q, _s));
      _p.y = d[i + 1] + 0.3; _s.set(R, 1, R);
      this.ring.setMatrixAt(k, _m.compose(_p, _q, _s));
      // heat drives the white core: big until ~0.45 s, then shrinks
      const heat = t < 0.45 ? 1 : Math.max(0, 1 - (t - 0.45) / 0.5);
      // centre alpha: solid → gone by ~0.75 s; rim alpha: solid → gone by 1.3 s (rim last)
      const aC = t < 0.4 ? 1 : Math.max(0, 1 - (t - 0.4) / 0.35);
      const aR = t < 0.6 ? 1 : Math.max(0, 1 - (t - 0.6) / 0.7);
      F[k * 4] = heat; F[k * 4 + 1] = d[i + 5]; F[k * 4 + 2] = Math.min(aC, aR); F[k * 4 + 3] = aR;
    }
    this.body.count = this.n; this.shell.count = this.n; this.ring.count = this.n;
    this.body.instanceMatrix.needsUpdate = true; this.shell.instanceMatrix.needsUpdate = true; this.ring.instanceMatrix.needsUpdate = true; this.dm.needsUpdate = true;
  }
  setGlow(on: boolean): void { this.gCore.value = on ? 2.6 : 1.5; this.gBody.value = on ? 1.1 : 1; }
}
const smooth = (u: number): number => u * u * (3 - 2 * u);
const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _q = new THREE.Quaternion();

// local ground tints for thrown rocks (the ground shader's soil / turf / lip colours, plus stone and char)
// soil / turf follow the map's debris palette (debrisLook.ts): no green turf off snow, ash or concrete
const SOIL_MEADOW = [0x8a6a48, 0x6e5238, 0xa98c5e, 0xb08c62].map((h) => new THREE.Color(h));
const TURF_MEADOW = [0x46ad48, 0x3a9a4c, 0x5cb850].map((h) => new THREE.Color(h));
const meadow = () => DEBRIS.look === DEFAULT_DEBRIS;
const STONE = [0x8f877a, 0x726c62, 0xa59c8c].map((h) => new THREE.Color(h));
const CHAR = [0x3a3024, 0x2c241b].map((h) => new THREE.Color(h));
const pick = (a: THREE.Color[]): THREE.Color => a[(Math.random() * a.length) | 0];

const THROWN_CAP = 8;

/** owner 2026-11-03 'make the evolved Zenith Orb model 10 times larger': render-only multiplier on Worldheart's drawn orb
 *  (charging + thrown); owner 2026-11-03 follow-up: "reduce the size of that evolved Zenith Orb by 5 times" (10 → 2). The
 *  sim size (ZENITH_EVO_SIZE), blast radius and damage are not read from it. */
export const ZENITH_EVO_VIS = 2;
const D2R = Math.PI / 180;
const _v = new THREE.Vector3(), _f = new THREE.Vector3();
interface Flight { pr: object | null; life0: number; seen: boolean; evo: boolean; ox: number; oy: number; oz: number; R0: number }

/**
 * Where a giant "sun" of radius R can hang over the hero at this camera: the largest R ≤ Rmax (down to Rmin) and the
 * smallest push away from the camera (s, metres along the camera's horizontal forward) such that
 *  - the camera stays well outside it (no near-plane clipping, never inside the shell),
 *  - it never covers the hero on screen (≥ 3° of clear sky between its limb and the hero),
 *  - its underside still reaches ≥ 5° into the top of the frame (it reads at every zoom level).
 * The orb's bottom sits 2.6 m + 5 % of R over the hero's head height (the original overhead charge placement).
 */
function fitSun(cam: THREE.PerspectiveCamera, hx: number, hy: number, hz: number, Rmax: number, Rmin: number, out: { R: number; s: number }): void {
  cam.getWorldDirection(_f);
  const fl = Math.hypot(_f.x, _f.z) || 1, fx = _f.x / fl, fz = _f.z / fl;
  const pitch = Math.asin(Math.max(-1, Math.min(1, -_f.y))), top = pitch - cam.fov * D2R / 2; // top edge, radians below horizontal
  const cx = cam.position.x, cy = cam.position.y, cz = cam.position.z;
  const hdx = hx - cx, hdy = hy + 1 - cy, hdz = hz - cz, hl = Math.hypot(hdx, hdy, hdz) || 1;
  for (let R = Rmax; R >= Rmin * 0.5; R *= 0.95) { // (at the closest zooms it may have to sit smaller than the sim orb to stay in view)
    for (let k = 0; k <= 16; k++) {
      const sh = R * k * 0.1;
      const px = hx + fx * sh, py = hy + 2.6 + R * 1.05, pz = hz + fz * sh;
      const vx = px - cx, vy = py - cy, vz = pz - cz, L = Math.hypot(vx, vy, vz);
      if (L < R * 1.15 + 1) continue;
      const a = Math.asin(R / L);
      const sep = Math.acos(Math.max(-1, Math.min(1, (vx * hdx + vy * hdy + vz * hdz) / (L * hl))));
      if (sep - a < 3 * D2R) continue;
      const below = -(Math.atan2(vy, Math.hypot(vx, vz)) - a); // the lower limb, radians below the horizon
      if (below - top < 5 * D2R) break; // pushing further only lifts it out of the frame
      out.R = R; out.s = sh; return;
    }
  }
  out.R = Rmin; out.s = 0;
}
const ROCK_CAP: Record<string, number> = { low: 80, medium: 140, high: 200 };
const DOME_CAP: Record<string, number> = { low: 6, medium: 8, high: 10 };
const QK: Record<string, number> = { low: 0.55, medium: 0.8, high: 1 };

export class ZenithFx {
  /** emission gain: follows the Glow setting (bloom on → emissive core, off → plain bands that still read) */
  private gain = mkGain();
  private orbMat: THREE.MeshBasicNodeMaterial;
  private haloMat: THREE.SpriteMaterial;
  private orbs: THREE.Mesh[] = []; private halos: THREE.Sprite[] = [];
  private used = 0;
  /** first-seen flight time per thrown orb, so the drawn orb can glide down from the charge height (render-only) */
  private flights: Flight[] = [];
  /** the view's camera (set by the view): the evolved orb is fitted to it and never allowed to swallow it */
  cam: THREE.PerspectiveCamera | null = null;
  /** evolved charge: fitted radius / push (smoothed) and the last drawn centre + radius (the throw starts from there) */
  private fit = { R: 0, s: 0 }; private fitOn = false; private last = { x: 0, y: 0, z: 0, R: 0, t: -1 };
  private rocks: Rocks;
  private domes: EnergyDome;
  private qk: number;

  constructor(scene: THREE.Scene, private fx: Fx, private ground: GroundDeform, private glow: GroundGlow, style: RenderStyle, quality: string) {
    this.orbMat = orbMaterial(this.gain);
    this.haloMat = new THREE.SpriteMaterial({ map: haloTexture(), transparent: true, depthWrite: false, fog: false });
    const geo = new THREE.SphereGeometry(1, 28, 20);
    for (let i = 0; i < THROWN_CAP; i++) {
      const o = new THREE.Mesh(geo, this.orbMat); o.visible = false; o.renderOrder = 3; o.frustumCulled = false;
      const h = new THREE.Sprite(this.haloMat); h.visible = false; h.renderOrder = 4;
      scene.add(o, h); this.orbs.push(o); this.halos.push(h);
      this.flights.push({ pr: null, life0: 1, seen: false, evo: false, ox: 0, oy: 0, oz: 0, R0: 0 });
    }
    this.qk = QK[quality] ?? 0.8;
    this.rocks = new Rocks(ROCK_CAP[quality] ?? 140, style, (x, z) => ground.sample(x, z), false, ZEN.aqua);
    this.domes = new EnergyDome(DOME_CAP[quality] ?? 8);
    scene.add(this.rocks.mesh, this.domes.body, this.domes.shell, this.domes.ring);
  }

  /** Re-skin the view's charging orb + halo with the Zenith palette (the view keeps placing them). */
  skinCharge(orb: THREE.Mesh, halo: THREE.Sprite): void {
    orb.material = this.orbMat;
    halo.material = this.haloMat;
  }

  setGlow(on: boolean): void { this.gain.value = on ? 1.35 : 1; this.domes.setGlow(on); }

  get rockCount(): number { return this.rocks.count; }

  // ---------- thrown orbs (per frame: begin → thrown()/split() → end) ----------
  begin(): void { this.used = 0; for (const f of this.flights) f.seen = false; }

  /**
   * Evolved (Worldheart) charge placement (owner 2026-11-03, ×ZENITH_EVO_VIS): a giant sun hanging over the hero. `size` is
   * the sim's full orb size, `g` the charge growth (0.12 → 1). It swells from just over the head and drifts up and away
   * from the camera as it grows, so at full size it hangs over the field ahead with the hero in clear view below it.
   * Writes the centre into `out` and returns the drawn radius. Render-only.
   */
  placeEvo(hx: number, hy: number, hz: number, size: number, g: number, dt: number, tNow: number, out: THREE.Vector3): number {
    const cam = this.cam, Rmax = size * ZENITH_EVO_VIS;
    if (!cam) { const R = Rmax * g; out.set(hx, hy + 2.6 + R * 1.05, hz); return R; }
    const want = { R: 0, s: 0 }; fitSun(cam, hx, hy, hz, Rmax, size, want);
    const k = this.fitOn ? 1 - Math.exp(-dt * 5) : 1; this.fitOn = true; // glide with zoom changes instead of popping
    this.fit.R += (want.R - this.fit.R) * k; this.fit.s += (want.s - this.fit.s) * k;
    cam.getWorldDirection(_f); const fl = Math.hypot(_f.x, _f.z) || 1;
    let R = this.fit.R * g; const sh = this.fit.s * g;
    out.set(hx + _f.x / fl * sh, hy + 2.6 + R * 1.05, hz + _f.z / fl * sh);
    R = this.camSafe(out, R);
    this.last.x = out.x; this.last.y = out.y; this.last.z = out.z; this.last.R = R; this.last.t = tNow;
    return R;
  }
  /** not charging: the next evolved charge snaps to its fit */
  idleEvo(): void { this.fitOn = false; }
  /** never let the shell reach the camera (near plane / inside the sphere) */
  private camSafe(c: THREE.Vector3, R: number, k = 1 / 1.15): number {
    if (!this.cam) return R;
    const L = c.distanceTo(this.cam.position);
    return Math.max(0.05, Math.min(R, (L - 1) * k));
  }

  private flight(pr: { life: number; out: number; kind: number; comet: boolean; x: number; y: number; z: number }): Flight {
    let f: Flight | null = null, free: Flight | null = null; // plain loops: no closures per frame
    for (const q of this.flights) { if (q.pr === pr) { f = q; break; } if (!free && q.pr === null) free = q; }
    if (!f) {
      f = free ?? this.flights[0]; f.pr = pr; f.life0 = Math.max(0.05, pr.life); f.evo = pr.comet && this.last.t >= 0;
      if (f.evo) { f.ox = this.last.x - pr.x; f.oy = this.last.y - pr.y; f.oz = this.last.z - pr.z; f.R0 = this.last.R; this.last.t = -1; }
    }
    f.seen = true;
    return f;
  }

  /** Drawn radius of a thrown orb (the same size as the fully charged orb, so it doesn't jump on release). Worldheart:
   *  starts at the giant charge radius and comes down to the sim orb size by the landing (the blast is the sim's). */
  orbR(pr: { out: number; r: number; kind: number; life: number; comet: boolean; x: number; y: number; z: number }): number {
    if (pr.kind === 3) return Math.max(0.45, pr.r * 0.085);
    const base = pr.out > 0 ? pr.out : pr.r * 0.2;
    if (pr.kind !== 4) return base;
    const f = this.flight(pr);
    if (!f.evo) return base;
    const u = Math.max(0, Math.min(1, pr.life / f.life0));
    _v.set(this.orbX(pr), this.orbY(pr), this.orbZ(pr));
    return this.camSafe(_v, base + (f.R0 - base) * u, 0.45); // in flight it comes toward the camera: never wider than ~27° of view
  }

  /** Drawn height: starts at the charge height above the head (the sim launches from 3.2 m) and glides onto the sim path
   *  by the landing (Worldheart: from the giant sun's centre, so it also glides in x / z; it lands exactly on the sim's spot). */
  orbY(pr: { y: number; life: number; out: number; kind: number; comet: boolean; x: number; z: number }): number {
    if (pr.kind !== 4) return pr.y;
    const f = this.flight(pr), w = Math.max(0, Math.min(1, pr.life / f.life0)) ** 2;
    if (f.evo) return pr.y + f.oy * w;
    const lift = Math.max(0, 2.6 + pr.out * 1.05 - 3.2);
    return pr.y + lift * w;
  }
  orbX(pr: { x: number; y: number; z: number; life: number; out: number; kind: number; comet: boolean }): number {
    if (pr.kind !== 4) return pr.x;
    const f = this.flight(pr); return f.evo ? pr.x + f.ox * Math.max(0, Math.min(1, pr.life / f.life0)) ** 2 : pr.x;
  }
  orbZ(pr: { x: number; y: number; z: number; life: number; out: number; kind: number; comet: boolean }): number {
    if (pr.kind !== 4) return pr.z;
    const f = this.flight(pr); return f.evo ? pr.z + f.oz * Math.max(0, Math.min(1, pr.life / f.life0)) ** 2 : pr.z;
  }

  /** Draw one thrown (or split) orb with its halo ring, and leave a cyan trail. */
  thrown(pr: { x: number; y: number; z: number; life: number; out: number; r: number; kind: number; comet: boolean }): void {
    if (this.used >= THROWN_CAP) return;
    const R = this.orbR(pr), x = this.orbX(pr), y = this.orbY(pr), z = this.orbZ(pr), o = this.orbs[this.used], h = this.halos[this.used]; this.used++;
    o.visible = true; o.position.set(x, y, z); o.scale.setScalar(R);
    h.visible = R < 8; h.position.set(x, y, z); h.scale.setScalar(R * 3.4); // (the giant Worldheart orb: no halo, it would wash the whole frame blue)
    const n = pr.kind === 3 ? 1 : 3, ps = Math.min(R, 6); // trail motes: the giant orb sheds the same few, just larger (capped)
    for (let k = 0; k < n; k++) {
      const col = k === 0 ? ZEN.aqua : k === 1 ? ZEN.cyan : ZEN.sky;
      this.fx.particles.emitDir(x + (Math.random() - 0.5) * R * 1.6, y + (Math.random() - 0.5) * R * 1.6, z + (Math.random() - 0.5) * R * 1.6, 0, 0, 0, col, 0.3 + ps * 0.35, 0.35, 2.5);
    }
  }

  end(): void {
    for (let i = this.used; i < THROWN_CAP; i++) { this.orbs[i].visible = false; this.halos[i].visible = false; }
    for (const f of this.flights) if (!f.seen) f.pr = null;
  }

  // ---------- impact ----------
  /** r = blast radius (m) = the dome radius; rank 1–5; evo = Worldheart; split = one of Worldheart's four split orbs. */
  impact(x: number, z: number, r: number, rank: number, evo: boolean, split: boolean): void {
    const fx = this.fx, gy = this.ground.sample(x, z), simR = r;
    if (split) r *= 0.4; // split orbs: small secondary pops (visual only; the sim blast radius is unchanged)
    const thin = Math.max(0.5, 1 - Math.max(CROWD.busy - 0.3, 0) * 1.2);
    // 1) brief white flash at the start
    fx.stars.add(x, gy + 1, z, Math.min(9, 1.5 + r * 0.5), 0.12, ZEN.white, 2.4);
    fx.flashes.add(x, gy + 0.5, z, r * 0.35, 0.14, 0xe8ffff, 1.0);
    fx.lights.flash(x, gy + 2, z, 0x9ff8ff, 30 + r * 3, 0.5, 8 + r * 1.8);
    this.glow.flash(x, z, r * 1.2, ZEN.cyan, 0.7, 0.7);
    // 2) the energy dome itself (radius = sim blast radius: small at rank 1, larger every rank)
    this.domes.add(x, gy, z, r);
    // 3) shock rings rolling outward along the ground: crisp light-cyan, then a wider blue-violet edge
    fx.rings.add(x, gy + 0.15, z, r * 1.5, 0.5, ZEN.rim, 2.4, 1);
    fx.rings.add(x, gy + 0.12, z, r * 1.8, 0.65, ZEN.violet, 1.6, 1.4);
    // 4) dust + debris spraying out at the dome's base along the ground
    const nd = Math.round(Math.min(26, 10 + r * 0.9) * thin);
    for (let k = 0; k < nd; k++) {
      const a = (k / nd) * 6.283 + Math.random() * 0.25, ca = Math.cos(a), sa = Math.sin(a), R0 = r * 0.8, sp = 5 + r * (0.9 + Math.random() * 0.6);
      fx.blast?.dust(x + ca * R0, gy + 0.4, z + sa * R0, ca * sp, 0.5 + Math.random(), sa * sp, 0.8 + Math.random() * 0.5, 0.4 + r * 0.06, 1.2 + r * 0.16, k % 3 === 0 ? 0.45 : 0);
    }
    for (let k = 0; k < Math.round(16 * thin); k++) {
      const a = Math.random() * 6.283, sp = 4 + r * (0.9 + Math.random() * 0.8);
      fx.particles.emitDir(x + Math.cos(a) * r * 0.8, gy + 0.3, z + Math.sin(a) * r * 0.8, Math.cos(a) * sp, 1 + Math.random() * 2, Math.sin(a) * sp, k % 3 ? ZEN.aqua : ZEN.white, 0.16 + r * 0.012, 0.45, 3);
    }
    // 5) rocks torn out of the crater: chunky chips + pebbles flung radially out and up, hot cyan rim for the first instant
    this.throwRocks(x, z, gy, split ? simR * 0.6 : r, rank, evo, split, thin);
    fx.shake = Math.min(1, Math.max(fx.shake, 0.18 + r * (split ? 0.012 : 0.025)));
  }

  private throwRocks(x: number, z: number, gy: number, r: number, rank: number, evo: boolean, split: boolean, thin: number): void {
    const q = this.qk * thin;
    const nBig = Math.round((split ? 6 : 12 + 1.5 * (rank - 1) + (evo ? 8 : 0)) * q);
    const nPeb = Math.round((split ? 12 : 22 + 2.5 * (rank - 1) + (evo ? 8 : 0)) * q);
    const burn = this.burnAt(x, z);
    for (let k = 0; k < nBig + nPeb; k++) {
      const big = k < nBig, a = Math.random() * 6.283, d0 = r * (0.12 + Math.random() * 0.4);
      const out = (3.5 + r * 0.85) * (0.6 + Math.random() * 0.8) * (big ? 1 : 1.25);
      const vy = big ? 6 + r * 0.3 + Math.random() * 5 : 7 + r * 0.35 + Math.random() * 7;
      const size = big ? Math.min(0.8, (0.22 + r * 0.02) * (0.7 + Math.random() * 0.6)) : (0.05 + r * 0.006) * (0.6 + Math.random() * 0.8);
      const u = Math.random();
      const col = burn > 0.5 && u < 0.6 ? pick(CHAR) : big ? (u < 0.42 ? pick(meadow() ? SOIL_MEADOW : DEBRIS.grit) : u < 0.72 ? pick(STONE) : pick(meadow() ? TURF_MEADOW : DEBRIS.turf)) : (u < 0.6 ? pick(meadow() ? SOIL_MEADOW : DEBRIS.grit) : pick(STONE));
      this.rocks.add(x + Math.cos(a) * d0, gy + 0.3 + Math.random() * 0.4, z + Math.sin(a) * d0, Math.cos(a) * out, vy, Math.sin(a) * out,
        size, big ? 2.6 + Math.random() * 0.8 : 1.4 + Math.random() * 0.8, col, big ? 0.45 + Math.random() * 0.15 : 0.3);
    }
  }

  private burnAt(x: number, z: number): number {
    const k = GROUND_RES / (GROUND_HALF * 2), i = Math.floor((x + GROUND_HALF) * k), j = Math.floor((z + GROUND_HALF) * k);
    if (i < 0 || j < 0 || i >= GROUND_RES || j >= GROUND_RES) return 0;
    return this.ground.burn[j * GROUND_RES + i];
  }

  /** Mark the scorch the Sim just left under a Zenith blast, so its glowing embers burn cyan instead of orange (props.ts). */
  tagScorches(scorches: readonly { x: number; z: number; r: number; born: number }[], tick: number, x: number, z: number): void {
    for (const sc of scorches) if (tick - sc.born <= 1 && (sc.x - x) ** 2 + (sc.z - z) ** 2 < Math.max(1, sc.r * 0.6) ** 2) (sc as { _zen?: boolean })._zen = true;
  }

  update(dt: number): void { this.rocks.update(dt); this.domes.update(dt); }
  clear(): void {
    this.rocks.clear(); this.domes.clear();
    for (const f of this.flights) f.pr = null;
    for (let i = 0; i < THROWN_CAP; i++) { this.orbs[i].visible = false; this.halos[i].visible = false; }
  }
}
